/**
 * SipRemoteAudio
 *
 * Renders a hidden audio element that plays remote call audio.
 * Watches activeSession and applies the selected output device (setSinkId) when supported.
 */

import Box from '@mui/material/Box';
import { useEffect, useRef } from 'react';
import { SessionState, Web } from 'sip.js';

import { useSipCallStore } from '../application/sipCallStore';
import { useWebrtcAudioDeviceStore } from '../application/webrtcAudioDeviceStore';

const REMOTE_AUDIO_ID = 'sip-remote-audio';

const supportsSetSinkId = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

export function SipRemoteAudio() {
  const activeSession = useSipCallStore(s => s.activeSession);
  const audioOutputDeviceId = useWebrtcAudioDeviceStore(s => s.audioOutputDeviceId);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const session = activeSession;
    const el = audioRef.current;
    if (!session || !el) {
      if (el) el.srcObject = null;
      return undefined;
    }

    const sdh = session.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;

    const attach = () => {
      const stream = sdh?.remoteMediaStream;
      const audioEl = audioRef.current;
      if (!audioEl || !stream) return;
      audioEl.srcObject = stream;
      if (supportsSetSinkId && audioOutputDeviceId) {
        (audioEl as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(audioOutputDeviceId).catch(() => undefined);
      }
      audioEl.play().catch(() => undefined);
    };

    const onState = (state: SessionState) => {
      if (state === SessionState.Established) attach();
    };

    session.stateChange.addListener(onState);
    if (session.state === SessionState.Established) attach();

    return () => {
      session.stateChange.removeListener(onState);
      if (audioRef.current) audioRef.current.srcObject = null;
    };
  }, [activeSession]);

  useEffect(() => {
    if (!supportsSetSinkId || !audioOutputDeviceId) return;
    const el = document.getElementById(REMOTE_AUDIO_ID) as HTMLAudioElement | null;
    if (!el?.srcObject) return;
    (el as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(audioOutputDeviceId).catch(() => undefined);
  }, [audioOutputDeviceId]);

  return (
    <Box component="span" sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden>
      <audio id={REMOTE_AUDIO_ID} ref={audioRef} autoPlay playsInline style={{ width: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />
    </Box>
  );
}
